/**
 * Copyright (c) Nicolas Gallagher.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * @flow
 */

import ReactNativePropRegistry from "./ReactNativePropRegistry"
import flattenStyle from "./flattenStyle"
import { StyleWrapper } from "./types"

const absoluteFillObject = {
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
}
const absoluteFill = ReactNativePropRegistry.register(absoluteFillObject)

const StyleSheet = {
  absoluteFill,
  absoluteFillObject,
  compose(style1: any, style2: any) {
    if (process.env.NODE_ENV !== "production") {
      /* eslint-disable prefer-rest-params */
      const len = arguments.length
      if (len > 2) {
        const readableStyles = [...arguments].map((a) => flattenStyle(a))
        throw new Error(
          `StyleSheet.compose() only accepts 2 arguments, received ${len}: ${JSON.stringify(
            readableStyles,
          )}`,
        )
      }
      /* eslint-enable prefer-rest-params */
    }

    if (style1 && style2) {
      return [style1, style2]
    }
    return style1 || style2
  },
  create<T extends { [key: string]: object }>(styles: T) {
    const result = {} as {
      [name in keyof T]: StyleWrapper<T[name]>
    }
    Object.keys(styles).forEach((name) => {
      const key: keyof T = name
      if (process.env.NODE_ENV !== "production") {
        const validate = require("./validate")
        const interopValidate = validate.default ? validate.default : validate
        interopValidate(key, styles)
      }
      const id = styles[key]
        ? ReactNativePropRegistry.register(styles[key])
        : undefined
      if (id) {
        result[key] = id
      }
    })
    return result
  },
  flatten: flattenStyle,

  // `hairlineWidth` is not implemented using screen density as browsers may
  // round sub-pixel values down to `0`, causing the line not to be rendered.
  hairlineWidth: 1,
}

export default StyleSheet
